import React, { useMemo, useState } from "react";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  KeyboardArrowDown,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  EMPTY_FILTERS,
  FILTER_SECTION_MAP,
  buildAppliedFilterChipData,
  normalizeFiltersForChips,
} from "./appliedFilterChipsUtils";

const AppliedFilterChipLabel = ({
  sectionLabel,
  fieldLabel,
  operatorLabel,
  valueLabel,
}) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      maxWidth: "100%",
      minWidth: 0,
    }}
  >
    {sectionLabel ? (
      <Typography
        component="span"
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: "#5C576B",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {sectionLabel}
      </Typography>
    ) : null}
    {fieldLabel ? (
      <Typography
        component="span"
        sx={{
          fontSize: 12,
          fontWeight: 500,
          color: "#5C576B",
          whiteSpace: "nowrap",
        }}
      >
        {fieldLabel}
      </Typography>
    ) : null}
    {operatorLabel ? (
      <Chip
        size="small"
        label={operatorLabel}
        sx={{
          height: 20,
          fontSize: 10,
          fontWeight: 600,
          bgcolor: "grey.200",
          color: "grey.700",
          borderRadius: "4px",
          flexShrink: 0,
          "& .MuiChip-label": {
            px: 0.75,
            py: 0,
          },
        }}
      />
    ) : null}
    {valueLabel !== "" && valueLabel != null ? (
      <Typography
        component="span"
        sx={{
          fontSize: 12,
          fontWeight: 500,
          color: "#5C576B",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
      >
        {valueLabel}
      </Typography>
    ) : null}
  </Box>
);

const transformGroupCondition = (groups = [], groupCondition = "AND", sectionConfig = {}) => {
  const transformedGroupCondition = [];
  groups.forEach((group) => {
    const conditions = (group?.data || []).map((condition) => {
      const fieldConfig = sectionConfig?.fields?.find(
        (field) => field?.name === condition?.category || field?.id === condition?.category
      );
      const operatorConfig = sectionConfig?.operators?.[fieldConfig?.datatype]?.find(
        (field) => field?.name === condition?.filter
      );
      return {
        type: "RULE_ITEM",
        member: fieldConfig ? fieldConfig.id : condition?.category,
        operator: operatorConfig ? operatorConfig.id : condition?.filter,
        values:
          condition?.userInput !== ""
            ? condition?.userInput
            : fieldConfig?.datatype === "int"
              ? [0]
              : [""],
      };
    });

    if (conditions.length > 0) {
      const outerKey = group?.operator?.toLowerCase() || groupCondition.toLowerCase();
      transformedGroupCondition.push({
        type: outerKey === "or" ? "OR_RULE" : "AND_RULE",
        [outerKey]: conditions,
      });
    }
  });
  return transformedGroupCondition;
};

const withRegeneratedCustomOutput = (updatedFilters, config) => {
  const nextFilters = JSON.parse(JSON.stringify(updatedFilters));
  Object.keys(FILTER_SECTION_MAP).forEach((sectionKey) => {
    const sectionConfigKey = FILTER_SECTION_MAP[sectionKey];
    const sectionConfig = config?.[sectionConfigKey];
    const sectionGroups = nextFilters?.[sectionKey]?.data ?? [];
    const sectionOperator = nextFilters?.[sectionKey]?.operator ?? "AND";
    const transformedOutput = transformGroupCondition(
      sectionGroups,
      sectionOperator,
      sectionConfig
    );
    nextFilters[sectionKey] = {
      ...(nextFilters?.[sectionKey] ?? {}),
      custom_output: transformedOutput.length
        ? {
            type: `${sectionOperator.toUpperCase()}_RULE`,
            [sectionOperator.toLowerCase()]: transformedOutput,
          }
        : [],
    };
  });
  return nextFilters;
};

const AppliedFiltersChips = ({
  filters = {},
  config = {},
  onChange,
  onSaveReport,
  onUpdateReport,
  currentSavedReport,
  hideSaveActions = false,
  readOnly = false,
}) => {
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [reportName, setReportName] = useState("");
  const [saveOptionsAnchorEl, setSaveOptionsAnchorEl] = useState(null);
  const normalizedFilters = useMemo(
    () => normalizeFiltersForChips(filters, config),
    [config, filters]
  );

  const chipData = useMemo(
    () => buildAppliedFilterChipData(filters, config),
    [config, filters]
  );

  const handleRemoveCondition = (sectionKey, groupId, conditionId) => {
    if (readOnly || !onChange) return;
    const updatedFilters = JSON.parse(
      JSON.stringify({ ...EMPTY_FILTERS, ...normalizedFilters })
    );
    const updatedGroups = (updatedFilters?.[sectionKey]?.data ?? [])
      .map((group) => {
        if (group?.id !== groupId) return group;
        return {
          ...group,
          data: (group?.data ?? []).filter(
            (condition) => condition?.id !== conditionId
          ),
        };
      })
      .filter((group) => (group?.data ?? []).length > 0);

    const nextFilters = {
      ...updatedFilters,
      [sectionKey]: {
      ...(updatedFilters?.[sectionKey] ?? {}),
      data: updatedGroups,
      },
    };
    onChange(withRegeneratedCustomOutput(nextFilters, config));
  };

  const handleClearAll = () => {
    if (readOnly || !onChange) return;
    onChange(JSON.parse(JSON.stringify(EMPTY_FILTERS)));
  };

  const handleSave = async () => {
    const parsedName = reportName.trim();
    if (!parsedName || !onSaveReport) return;
    await onSaveReport(parsedName);
    setSaveDialogOpen(false);
    setReportName("");
  };

  const handleUpdateReport = async () => {
    if (!currentSavedReport?.id || !onUpdateReport) {
      setSaveOptionsAnchorEl(null);
      return;
    }
    await onUpdateReport(currentSavedReport.id);
    setSaveOptionsAnchorEl(null);
  };

  if (!chipData.length) return null;

  return (
    <Box sx={{ mb: readOnly ? 0 : 2 }}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {chipData.map((chip) => (
          <Tooltip
            key={chip.key}
            title={
              chip.sectionLabel
                ? `${chip.sectionLabel} ${chip.label}`.trim()
                : chip.label
            }
            arrow
            placement="top"
            enterDelay={300}
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 420,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  fontSize: "12px",
                },
              },
            }}
          >
            <Chip
              label={
                <AppliedFilterChipLabel
                  sectionLabel={chip.sectionLabel}
                  fieldLabel={chip.fieldLabel}
                  operatorLabel={chip.operatorLabel}
                  valueLabel={chip.valueLabel}
                />
              }
              size="small"
              {...(readOnly
                ? {}
                : {
                    onDelete: () =>
                      handleRemoveCondition(
                        chip.sectionKey,
                        chip.groupId,
                        chip.conditionId,
                      ),
                    deleteIcon: (
                      <CancelIcon
                        sx={{ fontSize: "16px !important", color: "#757575" }}
                      />
                    ),
                  })}
              sx={{
                borderRadius: "10px",
                fontSize: "12px",
                height: "30px",
                maxWidth: 420,
                backgroundColor: "#F2F3F4",
                color: "#212121",
                "& .MuiChip-label": {
                  px: 1.5,
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                  maxWidth: "100%",
                },
                "& .MuiChip-deleteIcon": {
                  mr: 0.75,
                },
              }}
            />
          </Tooltip>
        ))}
        {!readOnly && !hideSaveActions && (
          <Button
            variant="outlined"
            size="small"
            onClick={(event) => setSaveOptionsAnchorEl(event.currentTarget)}
            endIcon={<KeyboardArrowDown fontSize="small" />}
            sx={{
              alignSelf: "center",
              textTransform: "none",
              borderRadius: "8px",
              borderColor: "rgba(0,0,0,0.3)",
              color: "#333333",
              fontWeight: 600,
              fontSize: "12px",
              px: 1.25,
              minWidth: "auto",
              height: "30px",
              "&:hover": {
                borderColor: "rgba(0,0,0,0.6)",
                backgroundColor: "#fafafa",
              },
            }}
          >
            Save
          </Button>
        )}
        {!readOnly && (
          <Typography
            onClick={handleClearAll}
            sx={{
              cursor: "pointer",
              color: "#D32F2F",
              fontWeight: 500,
              fontSize: "14px",
              alignSelf: "center",
              px: 1,
            }}
          >
            Clear All
          </Typography>
        )}
      </Stack>
      {!readOnly && (
        <>
          <Dialog
            open={saveDialogOpen}
            onClose={() => setSaveDialogOpen(false)}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle sx={{ fontSize: "18px", fontWeight: 600 }}>
              Save Report As
            </DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                autoFocus
                margin="dense"
                label="Report Name"
                value={reportName}
                inputProps={{ maxLength: 40 }}
                onChange={(event) => setReportName(event.target.value)}
                InputProps={{
                  endAdornment: (
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#999999",
                        ml: 1,
                      }}
                    >
                      {reportName.length}/40
                    </Typography>
                  ),
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleSave}
                disabled={!reportName.trim()}
                variant="contained"
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
          <Menu
            anchorEl={saveOptionsAnchorEl}
            open={Boolean(saveOptionsAnchorEl)}
            onClose={() => setSaveOptionsAnchorEl(null)}
          >
            <MenuItem
              sx={{
                fontSize: "14px",
                fontWeight: 500,
              }}
              onClick={handleUpdateReport}
              disabled={!currentSavedReport?.id}
            >
              Update Report
            </MenuItem>
            <MenuItem
              sx={{
                fontSize: "14px",
                fontWeight: 500,
              }}
              onClick={() => {
                setSaveDialogOpen(true);
                setSaveOptionsAnchorEl(null);
              }}
            >
              Save As New Report
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );
};

export default AppliedFiltersChips;
